document.addEventListener('DOMContentLoaded', () => {
    // Check if Firebase tools, specifically Firestore, are available
    if (!window.firebaseTools || !window.firebaseTools.db) {
        console.error("Firebase Firestore tools not found. Waitlist chat cannot initialize.");
        return;
    }

    const { db, doc, getDoc, setDoc, serverTimestamp } = window.firebaseTools;

    // --- DOM Elements ---
    const display = document.getElementById('waitlist-chat-display');
    const userInput = document.getElementById('waitlist-user-input');
    const sendBtn = document.getElementById('waitlist-send-btn');
    
    // --- State Management & Session Memory ---
    const conversationState = {
        ASKING_NAME: 'ASKING_NAME',
        ASKING_EMAIL: 'ASKING_EMAIL',
        ASKING_PHONE: 'ASKING_PHONE',
        AWAITING_CONFIRMATION: 'AWAITING_CONFIRMATION',
        ASKING_RESTART: 'ASKING_RESTART',
        COMPLETE: 'COMPLETE'
    };
    let currentState = conversationState.ASKING_NAME;
    let userProfile = { name: '', email: '', phone: '' };
    let sessionUser = ''; 

    // --- Marvin's Phrasebook for Varied Responses ---
    const marvinPhrases = {
        greetings: ["Hello! I'm M. I can add you to the early access list for our private beta.", "Greetings. I'm M, and I can get you on the waitlist.", "Hi there, I'm M. Ready to join the waitlist?"],
        askNameInitial: ["To start, what's your full name?", "First things first, may I have your full name?", "Let's begin. What's your full name?"],
        askEmail: ["A pleasure to meet you, {name}. What's the best email address to notify you at?", "Got it, {name}. And what's your primary email address?", "Perfect, {name}. Now, what email should I use for your invitation?"],
        askPhone: ["Thanks. Lastly, you can provide a phone number for SMS updates, or just hit Enter to skip.", "Great. If you'd like SMS updates, please provide a phone number. Otherwise, just press Enter.", "Almost done. You can enter a phone number for text alerts, or skip this step by hitting Enter."],
        askConfirmation: ["Great. Please confirm the details:", "Perfect. Just to double-check the information:", "Okay, let's review the details to make sure everything is correct:"],
        correction: ["My apologies. Let's try that again.", "No problem. We'll start this entry over.", "Understood. Let's correct that information."],
        success: ["Excellent, {name}. You're all set! I've added you to the waitlist and will be in touch soon.", "Perfect, {name}! You're on the list. We're excited to have you on board.", "All set, {name}! Your spot on the waitlist is confirmed. Talk to you soon."],
        duplicate: ["Ah, it looks like that email is already on the list! You're all set.", "It seems that email address has already been registered. No need to do anything else!", "I see that email is already in our system, so you're good to go."],
        askRestart: ["Would you like to add another person to the list? (yes or no)", "Can I help you add someone else to the list? (yes/no)", "Do you need to sign up another person?"],
        askNextNamePersonalized: ["Of course, {sessionUser}. Who is the next person we're adding?", "Happy to help, {sessionUser}. What's the full name for the next entry?", "You got it, {sessionUser}. Let's get the next person signed up. What's their name?"],
        askNextNameGeneric: ["Great! To begin, what is the full name of the next person?", "Okay, let's get started on the next one. What's their full name?"],
        goodbye: ["No problem! Have a great day.", "Alright. Thanks for stopping by!", "Sounds good. I'll be here if you need me."]
    };

    const getRandomPhrase = (phraseArray) => phraseArray[Math.floor(Math.random() * phraseArray.length)];

    // --- Core Functions (Defined ONCE here) ---
    const addMessageToDisplay = (text, sender) => {
        const messageDiv = document.createElement('div');
        messageDiv.classList.add('waitlist-message', `${sender}-message`);
        messageDiv.textContent = text;
        display.appendChild(messageDiv);
        display.scrollTop = display.scrollHeight;
    };

    const showTypingIndicator = () => {
        const typingDiv = document.createElement('div');
        typingDiv.id = 'typing-indicator';
        typingDiv.classList.add('waitlist-message', 'marvin-message', 'typing-indicator');
        typingDiv.innerHTML = `<span></span><span></span><span></span>`;
        display.appendChild(typingDiv);
        display.scrollTop = display.scrollHeight;
    };

    const hideTypingIndicator = () => {
        const indicator = document.getElementById('typing-indicator');
        if (indicator) {
            indicator.remove();
        }
    };

    const disableInput = (placeholderText) => {
        userInput.value = '';
        userInput.placeholder = placeholderText;
        userInput.disabled = true;
        sendBtn.disabled = true;
    };

    /**
     * Main conversation handler
     */
    const handleConversation = async (input) => {
        switch (currentState) {
            case conversationState.ASKING_NAME:
                if (input.length < 3) {
                    marvinAsks("That name seems a bit short. Could you please provide a full name?", 0);
                    return;
                }
                userProfile.name = input.split(' ').map(n => n.charAt(0).toUpperCase() + n.slice(1)).join(' '); // Capitalize name
                addMessageToDisplay(userProfile.name, 'user');
                currentState = conversationState.ASKING_EMAIL;
                askForEmail();
                break;
            
            case conversationState.ASKING_EMAIL:
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(input)) {
                    addMessageToDisplay(input, 'user');
                    marvinAsks("That doesn't look like a valid email. Could you try again?", 0);
                    return;
                }
                userProfile.email = input.toLowerCase().trim();
                addMessageToDisplay(userProfile.email, 'user');
                currentState = conversationState.ASKING_PHONE;
                askForPhone();
                break;

            case conversationState.ASKING_PHONE:
                userProfile.phone = input;
                addMessageToDisplay(userProfile.phone || '(Skipped)', 'user');
                currentState = conversationState.AWAITING_CONFIRMATION;
                askForConfirmation();
                break;

            case conversationState.AWAITING_CONFIRMATION:
                addMessageToDisplay(input, 'user');
                const confirmResponse = input.toLowerCase();
                if (['yes', 'y', 'correct', 'yep', 'yeah'].includes(confirmResponse)) {
                    const isNewUser = await createWaitlistEntry();
                    if (isNewUser) {
                        showConfirmation();
                    }
                } else {
                    marvinAsks(getRandomPhrase(marvinPhrases.correction), 500);
                    setTimeout(resetCurrentUser, 1500);
                }
                break;
            
            case conversationState.ASKING_RESTART:
                addMessageToDisplay(input, 'user');
                const restartResponse = input.toLowerCase();
                if (['yes', 'y', 'yep', 'yeah', 'ok'].includes(restartResponse)) {
                    resetConversation();
                } else {
                    marvinAsks(getRandomPhrase(marvinPhrases.goodbye), 500);
                    setTimeout(() => disableInput("Conversation complete."), 2000);
                }
                break;
        }
    };

    // --- Conversation Steps (Now using the Phrasebook) ---
    
    const marvinAsks = (message, delay = 1000) => {
        setTimeout(() => {
            hideTypingIndicator();
            const preformattedMessage = document.createElement('div');
            preformattedMessage.style.whiteSpace = 'pre-wrap';
            preformattedMessage.textContent = message;
            
            const messageDiv = document.createElement('div');
            messageDiv.classList.add('waitlist-message', `marvin-message`);
            messageDiv.appendChild(preformattedMessage);
            display.appendChild(messageDiv);
            display.scrollTop = display.scrollHeight;

            userInput.placeholder = "Your reply...";
            userInput.disabled = false;
            sendBtn.disabled = false;
            userInput.focus();
        }, delay);
        showTypingIndicator();
        disableInput("Marvin is typing...");
    };

    const startConversation = () => {
        marvinAsks(getRandomPhrase(marvinPhrases.greetings), 1000);
        setTimeout(() => marvinAsks(getRandomPhrase(marvinPhrases.askNameInitial)), 2500);
    };

    const askForEmail = () => {
        const phrase = getRandomPhrase(marvinPhrases.askEmail).replace('{name}', userProfile.name);
        marvinAsks(phrase);
    };

    const askForPhone = () => {
        marvinAsks(getRandomPhrase(marvinPhrases.askPhone));
    };
    
    const askForConfirmation = () => {
        const phoneDisplay = userProfile.phone ? userProfile.phone : 'Not provided';
        const intro = getRandomPhrase(marvinPhrases.askConfirmation);
        const confirmationMessage = `${intro}\n\nName: ${userProfile.name}\nEmail: ${userProfile.email}\nPhone: ${phoneDisplay}\n\nIs this correct? (yes/no)`;
        marvinAsks(confirmationMessage);
    };

    const showConfirmation = () => {
        if (!sessionUser) {
            sessionUser = userProfile.name.split(' ')[0];
        }
        const phrase = getRandomPhrase(marvinPhrases.success).replace('{name}', userProfile.name.split(' ')[0]);
        marvinAsks(phrase);
        setTimeout(() => {
             currentState = conversationState.ASKING_RESTART;
             marvinAsks(getRandomPhrase(marvinPhrases.askRestart), 0);
        }, 3000);
    };
    
    const showDuplicateMessage = () => {
        marvinAsks(getRandomPhrase(marvinPhrases.duplicate), 1000);
        currentState = conversationState.ASKING_RESTART;
        setTimeout(() => {
             marvinAsks(getRandomPhrase(marvinPhrases.askRestart), 0);
        }, 2500);
    };
    
    const resetCurrentUser = () => {
        userProfile = { name: '', email: '', phone: '' };
        currentState = conversationState.ASKING_NAME;
        marvinAsks("Let's start over. What is the full name?", 0);
    };

    const resetConversation = () => {
        userProfile = { name: '', email: '', phone: '' };
        currentState = conversationState.ASKING_NAME;
        if (sessionUser) {
            const phrase = getRandomPhrase(marvinPhrases.askNextNamePersonalized).replace('{sessionUser}', sessionUser);
            marvinAsks(phrase, 0);
        } else {
            marvinAsks(getRandomPhrase(marvinPhrases.askNextNameGeneric), 0);
        }
    };

    // --- Firestore Logic ---
    const createWaitlistEntry = async () => {
        const userDocRef = doc(db, "waiting_list", userProfile.email);
        try {
            const docSnap = await getDoc(userDocRef);
            if (docSnap.exists()) {
                console.log(`Duplicate waitlist attempt for: ${userProfile.email}`);
                showDuplicateMessage();
                return false; 
            }
            
            const dataToSave = {
                name: userProfile.name,
                email: userProfile.email,
                createdAt: serverTimestamp()
            };
            if (userProfile.phone) {
                dataToSave.phone = userProfile.phone;
            }

            await setDoc(userDocRef, dataToSave);
            console.log(`Waitlist entry created for: ${userProfile.email}`);
            return true;
        } catch (error) {
            console.error("Error checking/creating waitlist entry:", error);
            marvinAsks("I seem to have a connection issue. Please try refreshing the page.", 0);
            disableInput("An error occurred.");
            return false;
        }
    };
    
    // --- Event Listeners ---
    const handleUserInput = () => {
        const inputValue = userInput.value.trim();
        if (currentState === conversationState.ASKING_NAME && !inputValue) return;
        if (currentState === conversationState.COMPLETE) return;
        if (!inputValue && currentState !== conversationState.ASKING_PHONE) return;

        handleConversation(inputValue);
        userInput.value = '';
    };
    
    userInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleUserInput();
        }
    });

    sendBtn.addEventListener('click', handleUserInput);
    
    // --- Initialization ---
    startConversation();
});